import React, { useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
// Importa tu método de request
import { createTicket, createKontrolTicket } from "../request/request";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";

interface LabelValue {
	label: string;
	value: string;
}

interface HelpRequestModalProps {
	isOpen: boolean;
	onClose: () => void;
	onTicketCreated?: () => void;
}

// Opcional: lista de códigos de país
const countryCodes: LabelValue[] = [
	{ label: "United States (+1)", value: "+1" },
	{ label: "Mexico (+52)", value: "+52" },
	{ label: "Canada (+1)", value: "+1" },
];

// Tabs (si lo deseas en 2 pasos)
const TABS = [
	{ id: "generalInfo", label: "General Info" },
	{ id: "attachments", label: "Attachments & Response" },
];

export const HelpRequestModal: React.FC<HelpRequestModalProps> = ({
	isOpen,
	onClose,
	onTicketCreated,
}) => {
	const api = useAxiosAuth0();

	// --- ESTADOS DE FORMULARIO ---
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [attachmentFiles, setAttachmentFiles] = useState<FileList | null>(null);

	// Datos de contacto extra
	const [sendResponse, setSendResponse] = useState(false);
	const [contactName, setContactName] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [selectedCountryCode, setSelectedCountryCode] = useState("+52"); // Ej: por defecto +52
	const [contactPhoneNumber, setContactPhoneNumber] = useState("");

	// --- CONTROL DE UI ---
	const [activeTab, setActiveTab] = useState<string>("generalInfo");
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertVariant, setAlertVariant] = useState<"success" | "error">(
		"success"
	);
	const [alertMessage, setAlertMessage] = useState("");
	const [isSaving, setIsSaving] = useState(false);

	// Overlay si hay loading
	const isLoadingSomething = isSaving;
	const overlay = useProcessingOverlay(isLoadingSomething);

	// Check if the selected product is "Kontrol Edge"
	const globalProduct = useAppSelector(
		(state) => state.product.selectedProduct
	);
	const isKontrolEdge = globalProduct === "Kontrol Edge";

	// --- VALIDACIONES BÁSICAS ---
	const stripNonAlphanumeric = (str: string) => str.replace(/[\s\W_]+/g, "");
	const isValidEmail = (email: string) => {
		const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return pattern.test(email);
	};

	// --- REGLAS DE AVANCE ENTRE TABS ---
	const isGeneralInfoValid = () => {
		// Title >= 5 chars sin espacios/puntuación
		// Description >= 10 chars sin espacios/puntuación
		const titleClean = stripNonAlphanumeric(title);
		const descClean = stripNonAlphanumeric(description);
		return titleClean.length >= 5 && descClean.length >= 10;
	};

	const isAttachmentsValid = () => {
		// Solo validamos contacto si toggle = true
		if (!sendResponse) return true;
		const minName = contactName.trim().length >= 3;
		const validEmail = isValidEmail(contactEmail.trim());
		return minName && validEmail;
	};

	const canActivateTab = (tabId: string) => {
		switch (tabId) {
			case "generalInfo":
				return true;
			case "attachments":
				return isGeneralInfoValid();
			default:
				return false;
		}
	};

	const handleChangeTab = (tabId: string) => {
		if (canActivateTab(tabId)) {
			setActiveTab(tabId);
		}
	};

	// --- HANDLERS ---
	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			setAttachmentFiles(e.target.files);
		}
	};

	const handleCloseModal = () => {
		resetFormState();
		onClose();
	};

	const resetFormState = () => {
		setTitle("");
		setDescription("");
		setAttachmentFiles(null);
		setSendResponse(false);
		setContactName("");
		setContactEmail("");
		setSelectedCountryCode("+52");
		setContactPhoneNumber("");
		setActiveTab("generalInfo");
	};

	// --- GUARDAR TICKET ---
	const handleSave = async () => {
		if (!isAttachmentsValid()) return;
		setIsSaving(true);
		try {
			// Construimos el payload
			if (isKontrolEdge) {
				const finalPayload = {
					requesttype: "HELP_REQUEST",
					summary: title,
					description,
					files: attachmentFiles ? Array.from(attachmentFiles) : [],
				};
				await createKontrolTicket(api, finalPayload);
				if (onTicketCreated) {
					onTicketCreated();
				}
			} else {
				const finalPayload = {
					Tipo: "Help Request",
					Prioridad: 1,
					Resumen: title,
					Descripcion: description,
					files: attachmentFiles ? Array.from(attachmentFiles) : [],
					Nombre_de_contacto: contactName,
					Correo: contactEmail,
					phone_number: `${selectedCountryCode}${contactPhoneNumber.replace(
						/\s+/g,
						""
					)}`,
				};
				await createTicket(api, finalPayload);
			}
			setAlertVariant("success");
			setAlertMessage("Help Request created successfully!");
			setAlertOpen(true);
		} catch (err) {
			console.error("Error creating help request =>", err);
			setAlertVariant("error");
			setAlertMessage(
				"A problem occurred while creating the request, please try again."
			);
			setAlertOpen(true);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<GenericModal
			isOpen={isOpen}
			onClose={handleCloseModal}
			closeOnBackdropClick={false}
			size="lg"
			title={
				<div className="flex items-center gap-2">
					<span>Help Request</span>
					<GenericTooltip
						text="If you have any questions you can contact us through this medium."
						position="top"
					/>
				</div>
			}
		>
			{/* Encabezado de tabs */}
			<div className="border-b border-gray-200 mb-4">
				<nav className="flex space-x-4" aria-label="Tabs">
					{TABS.map((tab) => {
						const disabled = !canActivateTab(tab.id);
						return (
							<button
								key={tab.id}
								onClick={() => handleChangeTab(tab.id)}
								disabled={disabled}
								className={`
                  px-3 py-2 text-sm font-medium focus:outline-none
                  ${
										activeTab === tab.id
											? "text-blue-600 border-b-2 border-blue-600"
											: disabled
											? "text-gray-400 cursor-not-allowed"
											: "text-gray-500 hover:text-gray-700"
									}
                `}
							>
								{tab.label}
							</button>
						);
					})}
				</nav>
			</div>

			{/* TAB 1: General Info */}
			{activeTab === "generalInfo" && (
				<div className="space-y-4 text-sm text-gray-700">
					<div>
						<label className="block mb-1 font-medium flex items-center gap-1">
							Title <span className="text-red-500">*</span>
							<GenericTooltip
								text="At least 5 characters (ignoring spaces & punctuation)."
								position="top"
							/>
						</label>
						<GenericInput
							placeholder="Write a brief description"
							value={title}
							onChange={setTitle}
						/>
					</div>

					<div>
						<label className="block mb-1 font-medium flex items-center gap-1">
							Description <span className="text-red-500">*</span>
							<GenericTooltip
								text="At least 10 characters (ignoring spaces & punctuation)."
								position="top"
							/>
						</label>
						<textarea
							className="w-full h-24 px-3 py-2 border border-gray-300 rounded-sm text-sm"
							placeholder="Description of the problem (required)"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</div>

					<div className="flex justify-end pt-2">
						<GenericButton
							label="Next"
							variant="brand"
							onClick={() => handleChangeTab("attachments")}
							disabled={!isGeneralInfoValid()}
						/>
					</div>
				</div>
			)}

			{/* TAB 2: Attachments & Response */}
			{activeTab === "attachments" && (
				<div className="space-y-4 text-sm text-gray-700">
					<div>
						<label className="block mb-1 font-medium">
							Attach (Drag and drop screenshots or browse your device)
						</label>
						<div className="flex items-center gap-2">
							<label
								htmlFor="file-upload"
								className="cursor-pointer px-3 py-2 border border-gray-300 rounded-sm bg-gray-50 text-sm hover:bg-gray-100"
							>
								{attachmentFiles ? "Change Files" : "Choose Files"}
							</label>
							<input
								id="file-upload"
								type="file"
								className="hidden"
								multiple
								onChange={handleFileChange}
							/>
							<span className="text-sm text-gray-500">
								{attachmentFiles
									? Array.from(attachmentFiles)
											.map((file) => file.name)
											.join(", ")
									: "No file chosen"}
							</span>
						</div>
					</div>

					<div className="space-y-2">
						<div className="flex items-center justify-between mt-3">
							<span className="font-medium text-sm text-gray-800">
								I want to send a response of the case to another person
							</span>
							<GenericSwitch
								checked={sendResponse}
								onChange={setSendResponse}
							/>
						</div>

						{sendResponse && (
							<div className="space-y-4 mt-2 border border-gray-200 rounded p-3">
								<div className="flex gap-4">
									<div className="w-1/2">
										<label className="block mb-1 font-medium flex items-center gap-1">
											Contact Name <span className="text-red-500">*</span>
											<GenericTooltip
												text="At least 3 characters."
												position="top"
											/>
										</label>
										<GenericInput
											placeholder="Write the name here"
											value={contactName}
											onChange={setContactName}
										/>
									</div>
									<div className="w-1/2">
										<label className="block mb-1 font-medium flex items-center gap-1">
											Contact Email <span className="text-red-500">*</span>
											<GenericTooltip
												text="Must be a valid email format (e.g. name@domain.com)."
												position="top"
											/>
										</label>
										<GenericInput
											placeholder="Write the email here"
											value={contactEmail}
											onChange={setContactEmail}
											type="email"
										/>
									</div>
								</div>

								<div className="flex gap-4">
									<div className="w-1/2">
										<label className="block mb-1 font-medium">
											Country Code
										</label>
										<GenericDropdown
											items={countryCodes.map((c) => c.label)}
											value={
												countryCodes.find(
													(c) => c.value === selectedCountryCode
												)?.label || "United States (+1)"
											}
											onSelect={(label) => {
												const found = countryCodes.find(
													(c) => c.label === label
												);
												if (found) {
													setSelectedCountryCode(found.value);
												}
											}}
											defaultLabel="Select country code"
											minWidthClass=""
											maxWidthClass=""
											widthClass="w-full"
										/>
									</div>
									<div className="w-1/2">
										<label className="block mb-1 font-medium">
											Phone Number
										</label>
										<GenericInput
											placeholder="Phone number"
											value={contactPhoneNumber}
											onChange={setContactPhoneNumber}
										/>
									</div>
								</div>
							</div>
						)}
					</div>

					<div className="flex justify-end pt-2 gap-2">
						<GenericButton
							label="Back"
							variant="secondary"
							onClick={() => setActiveTab("generalInfo")}
						/>
						<GenericButton
							label="Save"
							variant="brand"
							onClick={handleSave}
							disabled={!isAttachmentsValid() || isSaving}
						/>
					</div>
				</div>
			)}

			{/* Overlay por si hay loading */}
			{overlay}

			{/* Alert de éxito/error */}
			<GenericAlert
				isOpen={alertOpen}
				onClose={() => setAlertOpen(false)}
				onConfirm={() => {
					setAlertOpen(false);
					if (alertVariant === "success") {
						handleCloseModal();
					}
				}}
				variant={alertVariant}
				title={alertVariant === "success" ? "Help Request Created" : "Error"}
				description={alertMessage}
				confirmLabel="OK"
			/>
		</GenericModal>
	);
};

export default HelpRequestModal;
